// src/containers/pages/caseManagement/components/DeleteCaseButton.tsx

import React from 'react';
import { Button, CircularProgress, Tooltip } from 'components/mui';
import DeleteIcon from '@mui/icons-material/Delete';
import type { DeleteButtonState } from '../utils/deleteButtonState';

interface DeleteCaseButtonProps {
  buttonState: DeleteButtonState;
  blockedTooltip: string;
  moveInProgress: boolean;
  deleting: boolean;
  onClick: () => void;
}

/**
 * DELETE button for the Source Case row (design §6).
 * grey (disabled+tooltip) / orange (warning) / red (error); hidden when no case.
 * Colors are theme tokens via MUI color props - never hex (UI_UX_STANDARDS).
 */
function DeleteCaseButton({
  buttonState,
  blockedTooltip,
  moveInProgress,
  deleting,
  onClick,
}: DeleteCaseButtonProps) {
  if (buttonState === 'hidden') return null;

  const disabled =
    buttonState === 'loading' ||
    buttonState === 'blocked' ||
    moveInProgress ||
    deleting;
  const color = buttonState === 'warn' ? 'warning' : 'error';
  const tooltip = moveInProgress
    ? 'Cannot delete while a move is in progress.'
    : buttonState === 'blocked'
      ? blockedTooltip
      : buttonState === 'loading'
        ? 'Checking whether this case can be deleted...'
        : '';

  const button = (
    <Button
      variant="contained"
      color={color}
      size="small"
      disabled={disabled}
      onClick={onClick}
      startIcon={
        buttonState === 'loading' || deleting ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <DeleteIcon />
        )
      }
      sx={{ minWidth: 110, fontWeight: 600 }}
    >
      {deleting ? 'Deleting...' : 'DELETE'}
    </Button>
  );

  // disabled MUI buttons don't fire hover - span-wrap (TableToolbar pattern);
  // tooltip REQUIRED on every disabled control (UI_UX_STANDARDS)
  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

export default DeleteCaseButton;
